/**
 * Defined by webpack for deployed bundles.  Keep this optional at runtime:
 * an older or partially cached bundle must never prevent the shared app
 * bootstrap (and, consequently, authentication) from running.
 */
declare const __JIDOKA_BUILD_ID__: string | undefined;

const injectedBuildId = typeof __JIDOKA_BUILD_ID__ === 'string'
  ? __JIDOKA_BUILD_ID__
  : 'unavailable';

export const UPDATE_AVAILABLE_EVENT = 'jidoka:update-available';
export const UPDATE_STARTED_EVENT = 'jidoka:update-started';
export const UPDATE_FAILED_EVENT = 'jidoka:update-failed';
export const BEFORE_UPDATE_EVENT = 'jidoka:before-update';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const CHANNEL_NAME = 'jidoka-app-version';
const STORAGE_KEY = 'JIDOKA_AVAILABLE_BUILD';

export interface BuildManifest {
  buildId: string;
  createdAt: string;
  assets: string[];
}

export interface UpdateAvailableDetail {
  currentBuildId: string;
  availableBuildId: string;
  detectedAt: number;
}

type DirtyCheck = () => boolean;

class VersionManager {
  readonly currentBuildId = injectedBuildId;
  private availableManifest: BuildManifest | null = null;
  private timer: number | null = null;
  private channel: BroadcastChannel | null = null;
  private dirtyChecks = new Set<DirtyCheck>();
  private dirtyForms = new Set<HTMLFormElement>();
  private started = false;
  private refreshing = false;

  get availableBuildId(): string | null {
    return this.availableManifest?.buildId || localStorage.getItem(STORAGE_KEY);
  }

  start(): void {
    if (this.started || !this.shouldRun()) return;
    this.started = true;
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    this.channel?.addEventListener('message', this.handleBroadcast);
    window.addEventListener('storage', this.handleStorage);
    window.addEventListener('focus', this.handleResume);
    window.addEventListener('online', this.handleResume);
    document.addEventListener('visibilitychange', this.handleVisibility);
    document.addEventListener('input', this.handleFormChange, true);
    document.addEventListener('change', this.handleFormChange, true);
    document.addEventListener('reset', this.handleFormReset, true);
    document.addEventListener('submit', this.handleFormSubmit, true);
    void this.checkNow();
    this.schedule();
  }

  destroy(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    this.channel?.removeEventListener('message', this.handleBroadcast);
    this.channel?.close();
    this.channel = null;
    window.removeEventListener('storage', this.handleStorage);
    window.removeEventListener('focus', this.handleResume);
    window.removeEventListener('online', this.handleResume);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    document.removeEventListener('input', this.handleFormChange, true);
    document.removeEventListener('change', this.handleFormChange, true);
    document.removeEventListener('reset', this.handleFormReset, true);
    document.removeEventListener('submit', this.handleFormSubmit, true);
    this.started = false;
  }

  registerDirtyCheck(check: DirtyCheck): () => void {
    this.dirtyChecks.add(check);
    return () => this.dirtyChecks.delete(check);
  }

  async checkNow(): Promise<boolean> {
    if (!this.shouldRun() || document.visibilityState === 'hidden' || !navigator.onLine) return false;
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Version request failed with ${response.status}`);
      const manifest = await response.json() as Partial<BuildManifest>;
      if (!manifest.buildId || typeof manifest.buildId !== 'string' || !Array.isArray(manifest.assets)) {
        throw new Error('Version manifest is malformed');
      }
      if (manifest.buildId === this.currentBuildId) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      if (this.availableManifest?.buildId === manifest.buildId) return true;
      this.availableManifest = manifest as BuildManifest;
      this.publishAvailable(manifest.buildId);
      return true;
    } catch (error) {
      console.warn('[Updates] Unable to check application version:', error);
      window.dispatchEvent(new CustomEvent(UPDATE_FAILED_EVENT, { detail: { error } }));
      return false;
    }
  }

  async refreshToLatest(): Promise<boolean> {
    if (this.refreshing) return false;
    const beforeEvent = new CustomEvent(BEFORE_UPDATE_EVENT, { cancelable: true });
    const customBlocked = !window.dispatchEvent(beforeEvent);
    const dirty = customBlocked || this.dirtyForms.size > 0 || [...this.dirtyChecks].some(check => {
      try { return check(); } catch { return true; }
    });
    if (dirty && !window.confirm('You have unsaved changes. Refresh and load the new version anyway?')) return false;

    this.refreshing = true;
    window.dispatchEvent(new CustomEvent(UPDATE_STARTED_EVENT, {
      detail: { currentBuildId: this.currentBuildId, availableBuildId: this.availableBuildId }
    }));
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }
      const urls = new Set<string>([window.location.href]);
      this.availableManifest?.assets.forEach(asset => urls.add(new URL(asset, location.origin).toString()));
      document.querySelectorAll<HTMLScriptElement>('script[src]').forEach(element => urls.add(element.src));
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]').forEach(element => urls.add(element.href));
      await Promise.allSettled([...urls].filter(value => new URL(value, location.href).origin === location.origin)
        .map(value => fetch(value, { cache: 'reload', credentials: 'same-origin' })));
      localStorage.removeItem(STORAGE_KEY);
      window.location.replace(window.location.href);
      return true;
    } catch (error) {
      this.refreshing = false;
      window.dispatchEvent(new CustomEvent(UPDATE_FAILED_EVENT, { detail: { error } }));
      return false;
    }
  }

  private shouldRun(): boolean {
    // Do not turn a missing build token into a permanent false-positive update
    // notification. This also makes a mixed/stale deployment fail safely.
    if (this.currentBuildId === 'unavailable') return false;
    const local = ['localhost', '127.0.0.1'].includes(location.hostname);
    return !local || localStorage.getItem('JIDOKA_ENABLE_UPDATE_CHECKS') === 'true'
      || new URLSearchParams(location.search).get('enableUpdateChecks') === '1';
  }

  private schedule(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = window.setInterval(() => void this.checkNow(), POLL_INTERVAL_MS);
  }

  private publishAvailable(buildId: string): void {
    localStorage.setItem(STORAGE_KEY, buildId);
    const detail: UpdateAvailableDetail = {
      currentBuildId: this.currentBuildId,
      availableBuildId: buildId,
      detectedAt: Date.now()
    };
    this.channel?.postMessage(detail);
    window.dispatchEvent(new CustomEvent<UpdateAvailableDetail>(UPDATE_AVAILABLE_EVENT, { detail }));
  }

  private handleBroadcast = (event: MessageEvent<UpdateAvailableDetail>): void => {
    if (event.data?.availableBuildId && event.data.availableBuildId !== this.currentBuildId) {
      window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT, { detail: event.data }));
    }
  };
  private handleStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY && event.newValue && event.newValue !== this.currentBuildId) {
      this.publishAvailable(event.newValue);
    }
  };
  private handleResume = (): void => { void this.checkNow(); };
  private handleVisibility = (): void => { if (document.visibilityState === 'visible') void this.checkNow(); };
  private handleFormChange = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const form = target?.closest('form');
    if (form) this.dirtyForms.add(form);
  };
  private handleFormReset = (event: Event): void => {
    if (event.target instanceof HTMLFormElement) this.dirtyForms.delete(event.target);
  };
  private handleFormSubmit = (event: Event): void => {
    if (event.target instanceof HTMLFormElement) window.setTimeout(() => this.dirtyForms.delete(event.target as HTMLFormElement), 0);
  };
}

export const versionManager = new VersionManager();

declare global {
  interface Window {
    JidokaUpdates: Pick<VersionManager, 'checkNow' | 'refreshToLatest' | 'registerDirtyCheck' | 'destroy'>;
  }
}

window.JidokaUpdates = versionManager;
